import React, { useState, useEffect } from 'react';
import { TrendingUp, Calendar, FileText, IndianRupee, CreditCard, ArrowUpRight, ArrowDownRight, Plus } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard({ onNavigate }) {
  const [analytics, setAnalytics] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics`),
        axios.get(`${API_URL}/api/invoices`)
      ]);
      setAnalytics(analyticsRes.data);
      setRecentInvoices(invoicesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const monthlyData = analytics?.monthly_sales || [];
  const lastMonthSales = monthlyData[monthlyData.length - 2]?.sales || 0;
  const currentMonthSales = summary.month_sales || 0;
  const monthGrowth = lastMonthSales > 0 ? ((currentMonthSales - lastMonthSales) / lastMonthSales * 100).toFixed(1) : 0;

  return (
    <div data-testid="dashboard" className="space-y-6 animate-fade-in">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          data-testid="dashboard-create-invoice"
          onClick={() => onNavigate('create')}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium text-sm shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </button>
        <button
          data-testid="dashboard-view-history"
          onClick={() => onNavigate('history')}
          className="flex items-center gap-2 px-4 py-2 bg-white text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors font-medium text-sm border border-zinc-200"
        >
          <FileText className="w-4 h-4" />
          View All Invoices
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today's Sales */}
        <div data-testid="metric-today-sales" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">Today's Sales</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.today_sales)}
              </p>
            </div>
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-emerald-700" />
            </div>
          </div>
        </div>

        {/* This Month */}
        <div data-testid="metric-month-sales" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">This Month</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.month_sales)}
              </p>
              <div className={`flex items-center gap-1 mt-1 text-sm ${monthGrowth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {monthGrowth >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(monthGrowth)}% vs last month</span>
              </div>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-700" />
            </div>
          </div>
        </div>

        {/* This Year */}
        <div data-testid="metric-year-sales" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">This Year</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.year_sales)}
              </p>
              <p className="text-sm text-zinc-500 mt-1">{summary.total_invoices || 0} invoices</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-purple-700" />
            </div>
          </div>
        </div>

        {/* EMI Sales */}
        <div data-testid="metric-emi-sales" className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-zinc-500">EMI Sales</p>
              <p className="text-2xl font-bold text-zinc-900 mt-1 font-[Manrope]">
                {formatCurrency(summary.total_emi_value)}
              </p>
              <p className="text-sm text-zinc-500 mt-1">{summary.emi_invoices || 0} EMI invoices</p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-amber-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Invoices & Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h3 className="text-lg font-semibold text-zinc-900">Recent Invoices</h3>
          </div>
          <div className="divide-y divide-zinc-100">
            {recentInvoices.length === 0 ? (
              <div className="px-6 py-8 text-center text-zinc-500">
                No invoices yet. Create your first invoice!
              </div>
            ) : (
              recentInvoices.map((invoice, index) => (
                <div 
                  key={invoice.id}
                  data-testid={`recent-invoice-${index}`}
                  className="px-6 py-4 hover:bg-zinc-50 transition-colors flex items-center justify-between"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-zinc-600" />
                    </div>
                    <div>
                      <p className="font-medium text-zinc-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-zinc-500">{invoice.customer_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-zinc-900 font-[Manrope]">
                      {formatCurrency(invoice.grand_total)}
                    </p>
                    <p className="text-sm text-zinc-500">
                      {new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          {recentInvoices.length > 0 && (
            <div className="px-6 py-3 bg-zinc-50 border-t border-zinc-200">
              <button
                onClick={() => onNavigate('history')}
                className="text-sm text-emerald-700 font-medium hover:text-emerald-800"
              >
                View all invoices →
              </button>
            </div>
          )}
        </div>

        {/* Invoice Type Distribution */}
        <div className="bg-white rounded-xl border border-zinc-200 p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">Invoice Types</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-600">GST Invoices</span>
                <span className="font-medium text-zinc-900">{analytics?.invoice_types?.gst || 0}</span>
              </div>
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${analytics?.invoice_types ? 
                      (analytics.invoice_types.gst / (analytics.invoice_types.gst + analytics.invoice_types.non_gst) * 100) || 0 
                      : 0}%` 
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-600">Non-GST Invoices</span>
                <span className="font-medium text-zinc-900">{analytics?.invoice_types?.non_gst || 0}</span>
              </div>
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${analytics?.invoice_types ? 
                      (analytics.invoice_types.non_gst / (analytics.invoice_types.gst + analytics.invoice_types.non_gst) * 100) || 0 
                      : 0}%` 
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* Top Products */}
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <h4 className="text-sm font-semibold text-zinc-900 mb-3">Top Products</h4>
            <div className="space-y-2">
              {(analytics?.product_sales || []).slice(0, 5).map((product, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-zinc-600 truncate flex-1 mr-2">{product.product}</span>
                  <span className="font-medium text-zinc-900">{formatCurrency(product.sales)}</span>
                </div>
              ))}
              {(!analytics?.product_sales || analytics.product_sales.length === 0) && (
                <p className="text-sm text-zinc-400">No product data yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
